import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { BrowserRouter } from "react-router-dom";
import { FileText, Upload, Mic, Send, Play, Pause, LogOut, ShieldCheck, Languages, ArrowRight, Menu, Sparkles, Clock3 } from "lucide-react";
import "@/App.css";
import "@/overrides.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const langs = [{id:"en",label:"English"},{id:"bn",label:"বাংলা"},{id:"hi",label:"हिन्दी"},{id:"ur",label:"اردو"},{id:"ar",label:"العربية"},{id:"ta",label:"தமிழ்"},{id:"te",label:"తెలుగు"},{id:"mr",label:"मराठी"},{id:"gu",label:"ગુજરાતી"},{id:"kn",label:"ಕನ್ನಡ"},{id:"ml",label:"മലയാളം"},{id:"pa",label:"ਪੰਜਾਬੀ"}];
const api = axios.create({baseURL:API,withCredentials:true});
const formatError = e => typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong. Please try again.";
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2,8)}`);

const DEMO_DOC = Object.freeze({id:"demo",name:"Q3-growth-report.pdf",pages:12,mime_type:"application/pdf",size:0,status:"ready",text:"Q3 Growth Report. Revenue grew by 24% across three markets. Customer retention was 91%. The strongest growth came from the enterprise segment in Q3."});
const DEMO_ANSWERS = {
  "key findings": "Revenue grew by 24% across three markets, customer retention held at 91%, and the enterprise segment led growth in Q3. (Source: sample Q3 report, page 12)",
  "summar": "The sample Q3 report highlights 24% revenue growth across three markets, 91% customer retention, and the enterprise segment as the top-performing segment of the quarter.",
};
const demoReply = (q) => {
  const lower = q.toLowerCase();
  const match = Object.keys(DEMO_ANSWERS).find(k => lower.includes(k));
  if (match) return DEMO_ANSWERS[match];
  if (lower.includes("retention")) return "Customer retention was 91% in the sample Q3 report.";
  if (lower.includes("revenue") || lower.includes("growth")) return "Revenue grew by 24% across three markets, with Q3 enterprise sales leading growth.";
  return "This is a demo document. Sign in and upload your own PDF or image to get grounded answers across all 12 languages.";
};

function Landing({onLogin, onDemo}) {
  return <main className="landing" data-testid="landing-page">
    <nav className="topnav">
      <div className="brand"><span className="brand-mark"><Sparkles size={16}/></span><span>DocVoice <b>AI</b></span></div>
      <div className="navlinks"><a href="#features" data-testid="features-link">Features</a><a href="#how" data-testid="how-it-works-link">How it works</a></div>
      <button className="nav-login" onClick={onLogin} data-testid="header-login-button">Log in <ArrowRight size={15}/></button>
    </nav>
    <section className="hero">
      <div className="hero-copy">
        <div className="eyebrow"><span className="pulse-dot"/> DOCUMENT INTELLIGENCE, IN YOUR VOICE</div>
        <h1>Talk to your<br/><em>documents</em> with AI.</h1>
        <p>Upload a PDF or image, ask questions using your voice or text, and get intelligent answers in your preferred language.</p>
        <div className="hero-actions">
          <button className="primary-btn" onClick={onDemo} data-testid="try-demo-button">Try Demo <ArrowRight size={17}/></button>
          <button className="text-btn" onClick={onLogin} data-testid="hero-login-button">Log in to your workspace</button>
        </div>
        <div className="trust-row"><ShieldCheck size={17}/><span>Your documents stay private and secure</span></div>
      </div>
      <div className="hero-visual">
        <div className="document-sheet">
          <div className="sheet-top"><span className="doc-icon"><FileText size={18}/></span><span>annual-report.pdf</span><span className="ready-badge">Ready</span></div>
          <div className="sheet-lines"><i/><i/><i className="short"/><div className="highlight">Revenue grew by 24% across three markets.</div><i/><i className="short"/></div>
        </div>
        <div className="answer-float">
          <div className="answer-label"><Sparkles size={13}/> ANSWERED IN ENGLISH</div>
          <strong>What were the key findings?</strong>
          <p>"Revenue grew by 24% across three markets, with the strongest growth in Q3."</p>
          <span className="page-ref">Page 12 of 48 <Play size={12} fill="currentColor"/></span>
        </div>
        <div className="voice-float"><Mic size={18}/><div><b>Listening...</b><small>Ask anything</small></div><span className="wave"><i/><i/><i/><i/></span></div>
      </div>
    </section>
    <section className="feature-strip" id="features">
      <div><Languages size={20}/><b>12 languages</b><span>Read and listen naturally</span></div>
      <div><FileText size={20}/><b>PDFs & images</b><span>One workspace for every file</span></div>
      <div><ShieldCheck size={20}/><b>Private by design</b><span>Only you can access your files</span></div>
    </section>
  </main>;
}

function Auth({onSuccess,onBack}) {
  const [mode,setMode] = useState("login");
  const [form,setForm] = useState({email:"",password:"",name:""});
  const [error,setError] = useState("");
  const submit = async e => {
    e.preventDefault(); setError("");
    try { const r = await api.post(`/auth/${mode}`, form); onSuccess(r.data); }
    catch (err) { setError(formatError(err)); }
  };
  return <div className="auth-page" data-testid="auth-page">
    <button className="back-link" onClick={onBack} data-testid="auth-back-button">← Back to DocVoice</button>
    <div className="auth-card">
      <div className="brand auth-brand"><span className="brand-mark"><Sparkles size={16}/></span>DocVoice <b>AI</b></div>
      <h1>{mode==="login"?"Welcome back":"Create your workspace"}</h1>
      <p>{mode==="login"?"Pick up where your documents left off.":"Start asking better questions of your files."}</p>
      <form onSubmit={submit} data-testid="auth-form">
        {mode==="register" && <input data-testid="auth-name-input" placeholder="Your name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>}
        <input data-testid="auth-email-input" type="email" placeholder="Email address" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
        <input data-testid="auth-password-input" type="password" placeholder="Password (8+ characters)" required minLength="8" value={form.password} onChange={e=>setForm({...form,password:e.target.value})}/>
        {error && <div className="error-message" data-testid="auth-error-message">{error}</div>}
        <button className="primary-btn full" data-testid="auth-submit-button">{mode==="login"?"Log in":"Create account"} <ArrowRight size={16}/></button>
      </form>
      <div className="auth-divider"><span>or</span></div>
      <button className="google-btn" disabled data-testid="google-login-button">Continue with Google <small>Setup required</small></button>
      <button className="switch-auth" onClick={()=>{setMode(mode==="login"?"register":"login");setError("")}} data-testid="auth-mode-toggle">
        {mode==="login"?"Need an account? Create one":"Already have an account? Log in"}
      </button>
    </div>
  </div>;
}

function EmptyWorkspace({onUpload,onDemo}) {
  return <div className="empty-state" data-testid="empty-workspace">
    <div className="empty-icon"><Upload size={25}/></div>
    <h2>Your document workspace</h2>
    <p>Upload a PDF or image to start a private conversation with your file.</p>
    <button className="primary-btn" onClick={onUpload} data-testid="empty-upload-button">Upload a document <Upload size={16}/></button>
    <button className="secondary-btn" onClick={onDemo} data-testid="workspace-demo-button">Explore the sample report</button>
  </div>;
}

function Chat({doc, demo, language, setLanguage}) {
  const [question,setQuestion] = useState("");
  const [messages,setMessages] = useState([]);
  const [loading,setLoading] = useState(false);
  const [playing,setPlaying] = useState(null);
  const [listening,setListening] = useState(false);
  const recognition = useRef(null);

  const pushMessage = (role, content) => setMessages(m => [...m, {id: uid(), role, content}]);
  const appendAssistant = (chunk, assistantId) => setMessages(m => m.map(x => x.id === assistantId ? {...x, content: x.content + chunk} : x));

  const send = async text => {
    const q = (text || question).trim();
    if (!q || loading) return;
    setQuestion("");
    pushMessage("user", q);
    setLoading(true);

    if (demo) {
      const assistantId = uid();
      setMessages(m => [...m, {id: assistantId, role: "assistant", content: ""}]);
      const reply = demoReply(q);
      // simulate streaming
      for (let i = 0; i < reply.length; i += 4) {
        await new Promise(r => setTimeout(r, 18));
        setMessages(m => m.map(x => x.id === assistantId ? {...x, content: reply.slice(0, i+4)} : x));
      }
      setLoading(false);
      return;
    }

    const assistantId = uid();
    setMessages(m => [...m, {id: assistantId, role: "assistant", content: ""}]);
    try {
      const res = await fetch(`${API}/documents/${doc.id}/chat`, {
        method: "POST", credentials: "include",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({question: q, language}),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", gotError = false;
      while (true) {
        const {value, done} = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, {stream: true});
        const blocks = buffer.split("\n\n"); buffer = blocks.pop();
        for (const block of blocks) {
          const eventLine = block.split("\n").find(l => l.startsWith("event:"));
          const dataLine = block.split("\n").find(l => l.startsWith("data:"));
          if (!eventLine || !dataLine) continue;
          const eventName = eventLine.slice(6).trim();
          let parsed;
          try { parsed = JSON.parse(dataLine.slice(5).trim()); } catch { continue; }
          if (eventName === "token") appendAssistant(parsed, assistantId);
          else if (eventName === "error") {
            gotError = true;
            setMessages(m => m.map(x => x.id === assistantId ? {...x, content: typeof parsed === "string" ? parsed : "The document assistant is unavailable right now. Please try again."} : x));
          }
        }
      }
      if (!gotError) {
        setMessages(m => m.map(x => x.id === assistantId && !x.content ? {...x, content: "No response received. Please try again."} : x));
      }
    } catch (err) {
      console.error("Chat request failed", err);
      setMessages(m => m.map(x => x.id === assistantId ? {...x, content: "The document assistant is unavailable right now. Please try again."} : x));
    } finally {
      setLoading(false);
    }
  };

  const mic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { pushMessage("assistant", "Voice input is not supported in this browser. You can type your question instead."); return; }
    if (listening) { recognition.current?.stop(); return; }
    recognition.current = new SR();
    recognition.current.lang = language === "en" ? "en-US" : `${language}-IN`;
    recognition.current.onstart = () => setListening(true);
    recognition.current.onend = () => setListening(false);
    recognition.current.onerror = () => { setListening(false); pushMessage("assistant", "I couldn't hear that. Please check microphone permission and try again."); };
    recognition.current.onresult = e => { const text = e.results[0][0].transcript; setQuestion(text); send(text); };
    recognition.current.start();
  };

  const speak = (text, id) => {
    if (playing === id) { window.speechSynthesis.cancel(); setPlaying(null); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = language === "en" ? "en-US" : `${language}-IN`;
    u.onend = () => setPlaying(null);
    u.onerror = () => setPlaying(null);
    window.speechSynthesis.speak(u);
    setPlaying(id);
  };

  return <div className="chat-panel" data-testid="chat-panel">
    <div className="chat-head">
      <div><span className="online-dot"/><b>Document assistant</b><small>Grounded in {doc.name}</small></div>
      <div className="language-select">
        <Languages size={14}/>
        <select value={language} onChange={e=>setLanguage(e.target.value)} data-testid="output-language-select">
          {langs.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
        </select>
      </div>
    </div>
    <div className="messages" data-testid="conversation-messages">
      {messages.length === 0 && (
        <div className="chat-welcome">
          <div className="assistant-avatar"><Sparkles size={18}/></div>
          <h3>Ask anything about this document</h3>
          <p>I'll stay grounded in the file and point you to the relevant page.</p>
          <div className="suggestions">
            <button onClick={()=>send("What are the key findings?")} data-testid="suggestion-key-findings">What are the key findings?</button>
            <button onClick={()=>send("Summarize this document")} data-testid="suggestion-summary">Summarize this document</button>
          </div>
        </div>
      )}
      {messages.map((m, i) => (
        <div className={`message ${m.role}`} key={m.id} data-testid={`${m.role}-message-${i}`}>
          {m.role === "assistant" && <div className="assistant-avatar"><Sparkles size={14}/></div>}
          <div className="bubble">
            {m.content || (m.role === "assistant" && loading ? "…" : "")}
            {m.role === "assistant" && m.content && (
              <div className="message-tools">
                <button onClick={()=>speak(m.content, m.id)} data-testid={`play-answer-${i}`}>
                  {playing === m.id ? <Pause size={13}/> : <Play size={13}/>} {playing === m.id ? "Pause" : "Play"}
                </button>
                <span>Source: {doc.name}</span>
              </div>
            )}
          </div>
        </div>
      ))}
      {loading && <div className="typing" data-testid="answer-loading"><i/><i/><i/> Reading your document...</div>}
    </div>
    <div className="composer">
      <button className={`mic-btn ${listening ? "listening" : ""}`} onClick={mic} title="Ask with your voice" data-testid="voice-input-button"><Mic size={20}/></button>
      <textarea value={question} onChange={e=>setQuestion(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} placeholder={listening?"Listening...":"Ask a question about this document"} data-testid="question-input"/>
      <button className="send-btn" onClick={()=>send()} disabled={!question.trim() || loading} data-testid="send-question-button"><Send size={18}/></button>
      <small>Enter to send · answers are never autoplayed</small>
    </div>
  </div>;
}

function Workspace({user, onLogout, onLoginFromDemo, demo = false}) {
  const [docs, setDocs] = useState([]);
  const [doc, setDoc] = useState(null);
  const [language, setLanguage] = useState("en");
  const [mobileTab, setMobileTab] = useState("document");
  const [notice, setNotice] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/documents");
      setDocs(r.data);
      if (r.data[0]) setDoc(r.data[0]);
    } catch (err) {
      console.error("Failed to load documents", err);
      setNotice("We couldn't load your documents. Please refresh and try again.");
    }
  }, []);

  useEffect(() => { if (!demo) load(); }, [demo, load]);

  const upload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setNotice("Processing your document...");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/documents/upload", fd);
      setDocs(d => [r.data, ...d]);
      setDoc(r.data);
      setNotice("Document ready — ask your first question.");
    } catch (err) {
      setNotice(formatError(err));
    }
  };

  return <div className="app-shell" data-testid="workspace-page">
    <aside className="sidebar">
      <div className="brand"><span className="brand-mark"><Sparkles size={15}/></span>DocVoice <b>AI</b></div>
      <div className="side-label">WORKSPACE</div>
      <button className="side-item active" data-testid="workspace-nav"><FileText size={17}/> My documents</button>
      <button className="side-item" data-testid="history-nav"><Clock3 size={17}/> Conversation history</button>
      <div className="sidebar-bottom">
        <div className="private-note"><ShieldCheck size={16}/><span>Private workspace<br/><small>Your files are yours alone</small></span></div>
        {user && (
          <div className="user-mini">
            <div>{user.name?.slice(0,1).toUpperCase()}</div>
            <span>{user.name}<small>{user.email}</small></span>
            <button onClick={onLogout} title="Log out" data-testid="logout-button"><LogOut size={15}/></button>
          </div>
        )}
      </div>
    </aside>
    <main className="workspace-main">
      <header className="workspace-header">
        <div><span className="eyebrow">YOUR WORKSPACE</span><h1>Good to see you, {user?.name?.split(" ")[0] || "there"}.</h1></div>
        <div className="header-actions">
          <input ref={fileRef} type="file" hidden accept="application/pdf,image/jpeg,image/png,image/webp" onChange={upload} data-testid="document-file-input"/>
          <button className="primary-btn upload-btn" onClick={()=>fileRef.current.click()} data-testid="upload-document-button"><Upload size={16}/> Upload document</button>
          <div className="mobile-menu-wrapper">
            <button className="menu-button" onClick={()=>setMobileMenuOpen(o=>!o)} data-testid="mobile-menu-button" aria-label="Open menu"><Menu size={20}/></button>
            {mobileMenuOpen && (
              <div className="mobile-menu" data-testid="mobile-menu">
                {!demo && user && <div className="mobile-menu-user"><b>{user.name}</b><small>{user.email}</small></div>}
                {demo
                  ? <button onClick={()=>{setMobileMenuOpen(false); onLoginFromDemo?.();}} data-testid="mobile-login-button"><LogOut size={14}/> Log in</button>
                  : <button onClick={()=>{setMobileMenuOpen(false); onLogout();}} data-testid="mobile-logout-button"><LogOut size={14}/> Log out</button>}
              </div>
            )}
          </div>
        </div>
      </header>
      {notice && <div className="notice" data-testid="upload-status-message">{notice}</div>}
      {demo && (
        <div className="demo-banner" data-testid="demo-banner">
          <Sparkles size={16}/> You're exploring a sample report.
          <button onClick={onLoginFromDemo} data-testid="demo-login-button">Log in to save your conversations</button>
        </div>
      )}
      {!doc && !demo ? (
        <EmptyWorkspace onUpload={()=>fileRef.current.click()} onDemo={()=>setDoc(DEMO_DOC)}/>
      ) : (
        <div className="document-workspace">
          <div className="mobile-tabs">
            <button className={mobileTab==="document"?"selected":""} onClick={()=>setMobileTab("document")} data-testid="document-tab">Document</button>
            <button className={mobileTab==="chat"?"selected":""} onClick={()=>setMobileTab("chat")} data-testid="chat-tab">AI chat</button>
          </div>
          <section className={`viewer-panel ${mobileTab==="chat"?"mobile-hidden":""}`} data-testid="document-viewer">
            <div className="viewer-toolbar"><span><FileText size={16}/> {doc?.name || DEMO_DOC.name}</span><span className="file-meta">{doc?.pages || DEMO_DOC.pages} pages</span></div>
            <div className="page-canvas">
              <div className="page-content">
                <div className="report-kicker">Q3 / 2024</div>
                <h2>Growth<br/><em>Report</em></h2>
                <div className="report-rule"/>
                <p>Performance highlights and strategic direction for the next quarter.</p>
                <div className="report-stat"><strong>24%</strong><span>Revenue growth across three markets</span></div>
                <div className="fake-paragraph"><i/><i/><i/><i className="short"/></div>
                <small>Page 01 / {doc?.pages || DEMO_DOC.pages}</small>
              </div>
            </div>
            <div className="page-controls">
              <button data-testid="previous-page-button">‹</button>
              <span>1 / {doc?.pages || DEMO_DOC.pages}</span>
              <button data-testid="next-page-button">›</button>
            </div>
          </section>
          <Chat doc={doc || DEMO_DOC} demo={demo} language={language} setLanguage={setLanguage}/>
        </div>
      )}
    </main>
  </div>;
}

const DEMO_USER = Object.freeze({name: "Guest", email: "Demo mode"});

function App() {
  const [view, setView] = useState("landing");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me")
      .then(r => { if (!cancelled) { setUser(r.data); setView("workspace"); } })
      .catch(err => { if (err?.response?.status !== 401) console.warn("Auth check failed", err); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, []);

  const handleLogout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("Logout failed", err); }
    setUser(null);
    setView("landing");
  }, []);

  const demoUser = useMemo(() => DEMO_USER, []);

  if (checking) return <div className="loading-screen" data-testid="app-loading"><Sparkles size={24}/></div>;
  if (view === "auth") return <Auth onSuccess={u => { setUser(u); setView("workspace"); }} onBack={()=>setView("landing")}/>;
  if (view === "demo") return <Workspace demo user={demoUser} onLogout={()=>setView("landing")} onLoginFromDemo={()=>setView("auth")}/>;
  if (view === "workspace") return <Workspace user={user} onLogout={handleLogout}/>;
  return <Landing onLogin={()=>setView("auth")} onDemo={()=>setView("demo")}/>;
}

export default function Root() { return <BrowserRouter><App/></BrowserRouter>; }
